import { campaignArtStatuses, campaignStatuses, designArtStatuses } from 'constants/statuses';
import isFailedBilling from 'utils/isFailedBilling';

const {
  NOT_SUBMITTED,
  ART_REQUIRED,
  ART_REVIEW,
  ART_ACCEPTED,
  ART_REJECTED,
  ART_LOCATION_SIGNED,
  ART_LOCATION_REJECTED
} = campaignArtStatuses;

export const parseCampaignArt = (campaignArt) => {
  const {
    id,
    created_at: createdDate,
    notes,
    text,
    uploads,
    status,
    type,
    campaign_id: campaignId,
    first_name: firstName,
    last_name: lastName
  } = campaignArt;

  const comments = notes.reverse().map((el) => {
    return {
      id: el.id,
      message: el.note,
      createdDate: el.created_at,
      username: el.sender_username
    };
  });
  const images = uploads
    ? uploads.map((el) => {
      return {
        fileId: el.fileName || el.imageId,
        fileName: el.fileName || el.imageName,
        fileDisplayName: el.fileDisplayName || el.imageName,
        fileUrl: el.fileUrl || el.imageUrl,
      };
    })
    : [];

  return {
    id,
    createdDate,
    comments,
    text,
    images,
    status,
    type,
    campaignId,
    name: `${firstName} ${lastName}`
  };
};

export const parseArtPreferences = (campaignArt) => {
  const {
    id,
    colors,
    text,
    concept,
    uploads,
    status,
    type,
    campaign_id: campaignId,
    phone,
    email,
    brand_name: brand,
    website,
    facebook,
    instagram,
    twitter,
    tiktok,
    snapchat
  } = campaignArt;

  const images = uploads
    ? uploads.map((el) => {
      return {
        fileId: el.fileName || el.imageId,
        fileName: el.fileName || el.imageName,
        fileDisplayName: el.fileDisplayName || el.imageName,
        fileUrl: el.fileUrl || el.imageUrl,
      };
    })
    : [];

  return {
    id,
    text,
    colors,
    concept,
    images,
    status,
    type,
    campaignId,
    phone,
    email,
    brand,
    website,
    facebook,
    instagram,
    twitter,
    tiktok,
    snapchat
  };
};

export const parseArtDesignStatus = (
  { art_design: artDesign, location_signoff: locationSignoff },
  campaignStatus,
  designArts,
  artPreferences,
  billingStatus,
  statusDescription = '',
) => {
  let status;

  const isArtPreferencesSubmitted = Boolean(artPreferences.id)
    || designArts.length > 0
    || parseInt(campaignStatus, 10) >= 3;
  const parseStatusValue = (value) => (typeof value === 'string' ? parseInt(value, 10) : value);
  // const locationRejectedStatusId = parseStatusValue(campaignStatuses.LOCATION_REJECTED);
  const productionApprovedThreshold = parseStatusValue(campaignStatuses.ART_PROOF_APPROVED);
  const campaignStatusId = parseStatusValue(campaignStatus);

  const isArtProofSubmitted = designArts.length > 0;
  const isNewArtProofSubmitted = designArts.filter((el) => el.status !== designArtStatuses.REJECTED).length > 0;
  const hasApprovedArt = designArts.some((el) => el.status === designArtStatuses.APPROVED);
  const isArtProofAccepted = hasApprovedArt || artDesign?.completed;
  const isArtProofRejected = campaignStatusId === parseStatusValue(campaignStatuses.ART_PROOF_REJECTED);
  const isPendingArtProof = campaignStatusId === parseStatusValue(campaignStatuses.PENDING_ART_PROOF);
  const isLocationApproved = campaignStatusId >= productionApprovedThreshold || locationSignoff?.completed;

  const normalizedStatusDescription = (statusDescription?.toLowerCase?.() || '').trim();
  const locationRejectedKeywords = [
    'location rejected',
    'art not installed',
    'location removed',
    'panel removed'
  ];
  const isLocationRejectedStatus = Boolean(normalizedStatusDescription)
    && locationRejectedKeywords.some((keyword) => normalizedStatusDescription.includes(keyword));

  if (isLocationRejectedStatus) {
    status = ART_LOCATION_REJECTED;
  } else if (!isArtPreferencesSubmitted) {
    status = NOT_SUBMITTED;
  } else if (
    (isArtPreferencesSubmitted && !isArtProofSubmitted)
    || (isPendingArtProof && !isArtProofAccepted && !isArtProofSubmitted)
    || (isArtProofSubmitted && isFailedBilling(billingStatus))
  ) {
    status = ART_REQUIRED;
  } else if (isArtProofRejected && !isArtProofAccepted) {
    status = ART_REJECTED;
  } else if (isNewArtProofSubmitted && !isArtProofAccepted) {
    status = ART_REVIEW;
  } else if (isArtProofAccepted && !isLocationApproved) {
    status = ART_ACCEPTED;
  } else if (isLocationApproved) {
    status = ART_LOCATION_SIGNED;
  }

  return status;
};

export const parseArtDesignsRequest = (request) => {
  return {
    id: request.id,
    description: request.description,
    attachments: JSON.parse(request.attachments),
    history: JSON.parse(request.comments) || [],
    status: request.status,
    priceQuote: request.price_quote,
    campaignId: request.campaign_id
  };
};
